import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { withTranslation, useTranslation } from 'react-i18next'
import { compose } from '../../common/helpers'
import { connect } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  Icon,
  Text,
  Page,
  Row,
  Col,
  Button,
  Pagination,
  Box,
  Link,
} from '../components'
import { OrdersTable } from '../components/order'
import { TextField, SelectField, MultiCheckboxField } from '../components/fields'
import StatusHistoryTooltip from '../components/StatusHistoryTooltip'
import { TinyPopTooltip } from '../components/Tooltip'
import { lists } from '../../common/lib/redux-lists'
import { setOrdersTableFullWidth } from '../../common/app/actions'
import { OrdersLoading } from '../components/Skeleton'
import { fetchBulkOrders } from '../../common/bulkOrder'
import { orderStatuses, orderListTimeTypes } from '../../common/config'
import { MIN_TOKEN_LENGTH } from '../../common/orders/searchTokens'
import { orderDateTranslate } from '../../common/orders/utils'
import { getTheme } from '../app/App'
import OrdersModeSwitcher from './OrdersModeSwitcher'
import BulkOrderListButton from './BulkOrderListButton'
import { useIsBulkOrderEnabled } from './useIsBulkOrderEnabled'

const OptionButton = styled(Button)`
  display: inline-block;
`

const OptionsWrapper = styled.div`
  display: flex;
  direction: row;
  justify-content: flex-end;
`

const initialStatusFilters = [
  orderStatuses.AT_MANUFACTURER,
  orderStatuses.DONE,
  orderStatuses.CANCELLED,
  orderStatuses.SENDING,
]

const BranchFilter = ({ isMasterUser, slaveUsers, list }) => {
  const { t } = useTranslation()
  if (!isMasterUser) return null

  const options = (slaveUsers || []).map(({ _id, username, name }) => ({
    value: _id,
    label: `${username} | ${name}`,
  }))

  return (
    <Col width="300px" style={{ zIndex: 1000 }}>
      <SelectField
        name="user.in"
        label={t('Branch')}
        value={list.filters['user.in']?.value}
        onChange={({ value }) => list.filters['user.in'].onChange({ value })}
        options={options}
        isClearable
      />
    </Col>
  )
}

BranchFilter.propTypes = {
  isMasterUser: PropTypes.bool,
  slaveUsers: PropTypes.array,
  list: PropTypes.object.isRequired,
}

BranchFilter.defaultProps = {
  isMasterUser: false,
  slaveUsers: [],
}

const BulkOrdersListPage = ({
  t,
  list,
  orders,
  meta,
  loading,
  isMasterUser,
  slaveUsers,
  setOrdersTableFullWidth: setFullWidth,
  ordersTableFullWidth,
  orderListTimeType,
}) => {
  const navigate = useNavigate()
  const goToDetail = row => {
    navigate(Link.BULK_ORDER_DETAIL.replace(':id', row._id))
  }
  const listHeader = (headerText, column, renderCol, sortByColumn, colProps) => ({
    headerText,
    column,
    onClick: list.headings[sortByColumn || column].onClick,
    sortState: list.headings[sortByColumn || column].direction,
    renderCol,
    colProps,
  })

  const paging = list.$paging(meta?.count)

  const hasDefaultFilters =
    initialStatusFilters.every(f => meta.filter?.status?.in?.includes(f)) ||
    !meta.filter?.status?.in

  const areFiltersSet = Boolean(
    meta?.filter?.searchText || meta?.filter?.user?.in?.length || !hasDefaultFilters,
  )
  const noOrdersWithFilter = orders && orders.length === 0 && areFiltersSet
  const noOrdersWithoutFilter = orders && orders.length === 0 && !areFiltersSet

  // search requires at least MIN_TOKEN_LENGTH chars; clear icon still reloads without search filter
  const searchValue = list.filters['searchText.like'].value || ''
  const isSearchValueValid = searchValue.trim().length >= MIN_TOKEN_LENGTH
  const SearchButtonWrap =
    !isSearchValueValid && !loading && searchValue.trim().length > 0 ? TinyPopTooltip : Fragment

  const reloadFromStart = () => {
    list.$setPage(0)
    setTimeout(() => {
      list.$reload()
    }, 10)
  }

  return (
    <Page metaTitle="Home">
      <Page.Content
        style={{
          paddingTop: '2rem',
          maxWidth: ordersTableFullWidth ? '100%' : undefined,
          transition: '0.3s',
        }}
      >
        <Box>
          <Box.Header>
            <OrdersModeSwitcher title={<Box.Title>{t('my orders')}</Box.Title>} />
          </Box.Header>
          <Box.HomepageBoxContent>
            <Row large justifyContent="space-between">
              <Row.Modern>
                <BulkOrderListButton />
              </Row.Modern>
              <Row.Modern>
                <Col>
                  <Button large onClick={() => setFullWidth(!ordersTableFullWidth)}>
                    <Icon size={23} name="CustomOppositeArrows" />
                  </Button>
                </Col>
              </Row.Modern>
            </Row>

            <>
              <Row alignItems="flex-end">
                <Col width="30%" marginRight="0.8rem">
                  <TextField
                    label={t('search')}
                    name="search"
                    value={list.filters['searchText.like'].value}
                    onChange={({ value }) => list.filters['searchText.like'].onChange({ value })}
                    icon="IosClose"
                    onKeyPress={event => {
                      if (event.key === 'Enter' && !loading && isSearchValueValid) {
                        reloadFromStart()
                      }
                    }}
                    onIconClick={() => {
                      list.filters['searchText.like'].onChange({ value: '' })
                      reloadFromStart()
                    }}
                  />
                </Col>
                <Col marginRight="2.4rem">
                  <SearchButtonWrap text={t('Type at least 3 characters to search')}>
                    <span style={{ display: 'inline-flex' }}>
                      <Button disabled={loading || !isSearchValueValid} onClick={reloadFromStart}>
                        {t('search')}
                      </Button>
                    </span>
                  </SearchButtonWrap>
                </Col>

                <BranchFilter
                  isMasterUser={isMasterUser}
                  slaveUsers={slaveUsers}
                  list={list}
                />
              </Row>
              <Row>
                <Col grow="0" style={{ paddingBottom: '0.3rem' }}>
                  <MultiCheckboxField
                    disabled={loading}
                    label={`${t('filter by state')}:`}
                    fields={[
                      {
                        label: t(orderStatuses.AT_MANUFACTURER),
                        name: orderStatuses.AT_MANUFACTURER,
                      },
                      { label: t(orderStatuses.DONE), name: orderStatuses.DONE },
                      { label: t(orderStatuses.CANCELLED), name: orderStatuses.CANCELLED },
                    ]}
                    value={list.filters['status.in'].value}
                    onChange={value => {
                      list.$setPage(0)
                      list.filters['status.in'].onChange({ value })
                    }}
                  />
                </Col>
              </Row>
            </>

            {loading && (!orders || orders.length === 0) && <OrdersLoading />}
            {!loading && noOrdersWithoutFilter && (
              <Row>
                <Text big>{t('orders list - no orders - welcome message')}</Text>
              </Row>
            )}
            {!loading && noOrdersWithFilter && (
              <Row>
                <Text big>{t('orders list - no orders - when filters set')}</Text>
              </Row>
            )}

            <Row marginBottom="0">
              {orders && orders.length > 0 && (
                <OrdersTable
                  isLoading={loading}
                  recalcHeaderWidth={!!ordersTableFullWidth}
                  data={orders}
                  columns={[
                    listHeader(t('order id'), 'manufacturerId', row => (
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => goToDetail(row)}
                      >
                        <Text>{row.manufacturerId || <>&nbsp;</>}</Text>
                      </div>
                    )),
                    listHeader(t('reference'), 'reference', row => (
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => goToDetail(row)}
                      >
                        <Text>{row.reference || ''}</Text>
                      </div>
                    )),
                    listHeader(
                      orderListTimeType === orderListTimeTypes.SENT_ONLY
                        ? t('sent at')
                        : t('time'),
                      'dateText',
                      row => {
                        const data = orderDateTranslate({ order: row, t, orderListTimeType })
                        return (
                          <StatusHistoryTooltip order={row}>
                            <Text>{data}</Text>
                          </StatusHistoryTooltip>
                        )
                      },
                      orderListTimeType === orderListTimeTypes.SENT_ONLY
                        ? 'exportSentAt'
                        : 'sortDate',
                    ),
                    listHeader(t('status'), 'status', row => {
                      let color = null
                      if (
                        row.status === orderStatuses.AT_MANUFACTURER ||
                        row.status === orderStatuses.SENT
                      ) {
                        color = getTheme().colors.green
                      }
                      return (
                        <StatusHistoryTooltip order={row}>
                          <Text color={color}>
                            {t(row.status)}
                            {row.status === orderStatuses.AT_MANUFACTURER &&
                              ` / ${t(row.subStatus)}`}
                          </Text>
                        </StatusHistoryTooltip>
                      )
                    }),
                    isMasterUser
                      ? {
                          headerText: t('Branch'),
                          colProps: {
                            style: { wordWrap: 'break-word' },
                          },
                          renderCol: row => (
                            <Text style={{ opacity: row.mine ? 0.5 : 1 }}>
                              {row.user && row.user.username}
                              {row.mine && ` (${t('orders list - me')})`}
                            </Text>
                          ),
                        }
                      : undefined,
                    {
                      headerText: t('actions'),
                      colProps: {
                        style: { width: '1%' },
                      },
                      renderCol: row => (
                        <OptionsWrapper>
                          <OptionButton
                            onClick={e => {
                              e.stopPropagation()
                              goToDetail(row)
                            }}
                          >
                            {t('detail')}
                          </OptionButton>
                        </OptionsWrapper>
                      ),
                    },
                  ]}
                />
              )}
            </Row>

            {orders.length > 0 && <Pagination paging={paging} />}
          </Box.HomepageBoxContent>
        </Box>
      </Page.Content>
    </Page>
  )
}

BulkOrdersListPage.propTypes = {
  t: PropTypes.func.isRequired,
  fetchBulkOrders: PropTypes.func.isRequired,
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  list: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  isMasterUser: PropTypes.bool.isRequired,
  slaveUsers: PropTypes.array,
  setOrdersTableFullWidth: PropTypes.func.isRequired,
  ordersTableFullWidth: PropTypes.bool,
  orderListTimeType: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
}

BulkOrdersListPage.defaultProps = {
  slaveUsers: [],
  ordersTableFullWidth: false,
}

const enhance = compose(
  withTranslation(),
  connect(
    state => {
      const { history } = state.bulkOrder
      return {
        orders: history.orders,
        meta: history.meta,
        loading: history.loading,
        isMasterUser: history.isMasterUser,
        slaveUsers: history.slaveUsers,
        ordersTableFullWidth: state.app.ordersTableFullWidth,
        orderListTimeType: state.config.orderListTimeType,
      }
    },
    {
      fetchBulkOrders,
      setOrdersTableFullWidth,
    },
  ),
  lists({
    path: 'BulkOrdersListPage',
    filters: ['status.in', 'searchText.like', 'user.in'],
    noAutoRefreshFields: ['searchText.like'],
    headings: ['reference', 'manufacturerId', 'sortDate', 'exportSentAt', 'status'],
    debounceTime: 1,
    loadDataAction: props => query => {
      const copyQuery = JSON.parse(JSON.stringify(query))
      // Pair-style: SENT is bundled with AT_MANUFACTURER (exported, awaiting first tracking).
      if (
        copyQuery?.filters?.status?.in?.includes?.(orderStatuses.AT_MANUFACTURER) &&
        !copyQuery.filters.status.in.includes(orderStatuses.SENT)
      ) {
        copyQuery.filters.status.in.push(orderStatuses.SENT)
      }
      // Always include in-flight exports (pair always appends SENDING / WAIT_FOR_SEND).
      // Guard: persisted/cleared filters may omit status.in (prepareQuery skips empty).
      if (!copyQuery.filters) copyQuery.filters = {}
      if (!copyQuery.filters.status) copyQuery.filters.status = {}
      if (!Array.isArray(copyQuery.filters.status.in)) {
        copyQuery.filters.status.in = []
      }
      if (!copyQuery.filters.status.in.includes(orderStatuses.SENDING)) {
        copyQuery.filters.status.in.push(orderStatuses.SENDING)
      }
      return props.fetchBulkOrders(copyQuery)
    },
    getInitialState: ({ orderListTimeType }) => {
      const sort =
        orderListTimeType === orderListTimeTypes.SENT_ONLY ? '-exportSentAt' : '-sortDate'
      return {
        'status.in': initialStatusFilters,
        sort,
      }
    },
  }),
)

const EnhancedBulkOrdersListPage = enhance(BulkOrdersListPage)

const BulkOrdersListPageGate = props => {
  const isBulkOrderEnabledForUser = useIsBulkOrderEnabled()

  if (!isBulkOrderEnabledForUser) {
    return <Navigate to={Link.HOME} replace />
  }
  return <EnhancedBulkOrdersListPage {...props} />
}

export default BulkOrdersListPageGate
